import { useCallback, useEffect, useRef, useState } from 'react';
import { commandCenterApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { LoadState, openInApp } from './parts';
import { Dash, fmt } from './statsParts';

// הזנת ח.פ. ידנית — the suppliers the automatic backfill must not decide.
// Opens on "החברה המרכזית" (owner, 27/09/2026: every supplier by that name is
// entered by hand); any other name can be searched, e.g. to settle a conflict
// the backfill report flagged. Writes suppliers.business_id, the same field
// the customer app's supplier form edits (lib/ccSupplierIds.js).

const DEFAULT_QUERY = 'החברה המרכזית';
const digitsOnly = (v) => (v || '').replace(/[\s-]/g, '');
const isValid = (v) => digitsOnly(v) === '' || /^\d{9}$/.test(digitsOnly(v));

function useSupplierIdRows(q) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const token = useRef(0);
  const load = useCallback(async () => {
    const mine = ++token.current;
    setError(false);
    try {
      const r = await commandCenterApi.getSupplierIds(q);
      if (mine === token.current) setRows(r.data.rows);
    } catch {
      if (mine === token.current) { setError(true); setRows(null); }
    }
  }, [q]);
  // Typing narrows as you go; a short pause before asking the server.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);
  return { rows, setRows, error, load };
}

// The numbers this supplier's documents printed — a hint to copy, not a value.
function PrintedHints({ printed, onPick }) {
  if (!printed.length) return <Dash />;
  return (
    <span className="cc-reasons">
      {printed.map(p => (
        <button key={p.business_id} type="button" className="cc-badge cc-reason cc-hint"
          title="העתק לשדה" onClick={() => onPick(p.business_id)}>
          {p.business_id} · {fmt(p.documents)} מסמכים
        </button>
      ))}
    </span>
  );
}

// `seed` pre-fills the field from a picked hint; the row keys the editor by
// it, so picking re-seeds and typing afterwards edits as usual.
function IdEditor({ row, seed, onSaved }) {
  const [value, setValue] = useState(seed ?? row.business_id);
  const [saving, setSaving] = useState(false);
  const dirty = digitsOnly(value) !== row.business_id;
  const valid = isValid(value);

  const save = async () => {
    setSaving(true);
    try {
      const r = await commandCenterApi.setSupplierId(row.id, digitsOnly(value));
      setValue(r.data.business_id);   // show what was stored: "514-000-001" → "514000001"
      onSaved(row.id, r.data.business_id);
      toast(r.data.business_id ? `נשמר ח.פ. ${r.data.business_id}` : 'הח.פ. נמחק');
    } catch (err) {
      toast(err.response?.data?.error || 'השמירה נכשלה — אפשר לנסות שוב', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="cc-id-editor">
      <input
        className={`form-control cc-id-input${valid ? '' : ' is-invalid'}`}
        value={value}
        inputMode="numeric"
        dir="ltr"
        placeholder="9 ספרות"
        aria-label={`ח.פ. של ${row.name} ב${row.restaurant_name}`}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && dirty && valid && !saving) save(); }}
      />
      {dirty ? (
        <button type="button" className="btn btn-sm btn-success" disabled={!valid || saving} onClick={save}>
          {saving ? 'שומר…' : 'שמור'}
        </button>
      ) : row.business_id ? <Icon name="check" size={16} className="yes-ic" /> : null}
      {!valid && <span className="cc-field-error">9 ספרות</span>}
    </span>
  );
}

function IdRow({ row, onSaved }) {
  const [seed, setSeed] = useState(null);
  return (
    <tr>
      <td>{row.restaurant_name}</td>
      <td style={{ fontWeight: 600 }}>{row.name}</td>
      <td className="cc-num">{row.docs ? fmt(row.docs) : <Dash />}</td>
      <td><PrintedHints printed={row.printed} onPick={setSeed} /></td>
      <td>
        <IdEditor key={seed ?? 'own'} row={row} seed={seed}
          onSaved={(id, v) => { setSeed(null); onSaved(id, v); }} />
      </td>
      <td>
        {row.latest_document ? (
          <button type="button" className="btn btn-sm btn-outline"
            onClick={() => openInApp(`/document/${row.latest_document.id}`, row.restaurant_id)}>
            <Icon name="external" size={13} /> {row.latest_document.doc_num || 'מסמך'}{row.latest_document.date ? ` · ${row.latest_document.date}` : ''}
          </button>
        ) : <Dash />}
      </td>
    </tr>
  );
}

export default function SupplierIdsSection({ onSaved }) {
  const [q, setQ] = useState(DEFAULT_QUERY);
  const { rows, setRows, error, load } = useSupplierIdRows(q);
  const saved = (id, businessId) => {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, business_id: businessId } : r)));
    onSaved?.();
  };
  const withId = rows ? rows.filter(r => r.business_id).length : 0;

  return (
    <section className="cc-tabpanel-section">
      <div className="cc-card-head" style={{ flexWrap: 'wrap' }}>
        <h2 className="cc-card-title">הזנת ח.פ. ידנית</h2>
        {rows && (
          <span className="cc-muted" style={{ fontSize: '0.79rem' }}>
            {fmt(rows.length)} ספקים · {fmt(withId)} עם ח.פ.
          </span>
        )}
        <span className="cc-spacer" />
        <input className="form-control" style={{ maxWidth: 220 }} value={q} onChange={e => setQ(e.target.value)}
          placeholder="שם ספק…" aria-label="חיפוש ספק להזנת ח.פ." />
      </div>
      <LoadState loading={!rows && !error} error={error} onRetry={load}
        empty={rows?.length === 0} emptyMessage={q.trim() ? 'אין ספק בשם הזה' : 'הקלידו שם ספק'}>
        {rows && (
          <div className="cc-matrix-scroll">
            <table className="cc-list">
              <thead>
                <tr>
                  <th>מסעדה</th><th>ספק</th><th>מסמכים</th>
                  <th title="מה שנקרא מהמסמכים של הספק — לחיצה מעתיקה לשדה">ח.פ. שנקרא מהמסמכים</th>
                  <th>ח.פ.</th><th>מסמך אחרון</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => <IdRow key={r.id} row={r} onSaved={saved} />)}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
    </section>
  );
}
